'use client'

import * as React from 'react'

import { FieldWrapper } from '@/components/form/field-wrapper'
import { useFieldContext } from '@/components/form/form'

import type { MultiComboboxProps } from '../ui/combobox/multi-combobox-types'

import { MultiCombobox as MultiComboboxBase } from '../ui/combobox/multi-combobox'

import type { InfiniteQueryOptions, RegularQueryOptions } from '@/types/query'

export type MultiComboboxFieldProps<T extends object> = Omit<
  MultiComboboxProps<T>,
  'onChange' | 'value'
>

export const MultiCombobox = <T extends object>(
  props: MultiComboboxFieldProps<T>
): React.ReactElement => {
  const {
    classNames,
    infinite,
    initialItems,
    label,
    options,
    queryOptions,
    required,
    ...restProps
  } = props

  const field = useFieldContext<string[]>()

  const value = field.state.value ?? []
  const handleChange = React.useCallback(
    (newValue: string[]) => {
      field.handleChange(newValue)
    },
    [field]
  )

  const mergedProps = React.useMemo<MultiComboboxProps<T>>(() => {
    const baseProps = {
      ...restProps,
      classNames,
      initialItems,
      onChange: handleChange,
      value
    }

    if (infinite) {
      return {
        ...baseProps,
        infinite: true as const,
        queryOptions: queryOptions as InfiniteQueryOptions<T>
      } as MultiComboboxProps<T>
    }

    if (options) {
      return {
        ...baseProps,
        infinite: false as const,
        options
      } as MultiComboboxProps<T>
    }

    return {
      ...baseProps,
      infinite: false as const,
      queryOptions: queryOptions as RegularQueryOptions<T>
    } as MultiComboboxProps<T>
  }, [
    infinite,
    options,
    queryOptions,
    restProps,
    classNames,
    initialItems,
    value,
    handleChange
  ])

  return (
    <FieldWrapper
      classNames={classNames}
      field={field}
      label={label}
      required={required}
    >
      <MultiComboboxBase {...mergedProps} />
    </FieldWrapper>
  )
}
